import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import RouteCard from "../components/RouteCard";
import Pagination from "../components/Pagination";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { MagnifyingGlass } from "@phosphor-icons/react";

const filters = ["All", "Easy", "Moderate", "Hard", "Expert"];
const UNKNOWN_COUNTRY = "__unknown_country__";

function normalizeCountryValue(country) {
  if (typeof country !== "string") return UNKNOWN_COUNTRY;
  const trimmed = country.trim();
  return trimmed || UNKNOWN_COUNTRY;
}

const distanceBuckets = [
  { label: "Any distance", value: "any", min: 0, max: Infinity },
  { label: "< 200 km", value: "short", min: 0, max: 200 },
  { label: "200 – 500 km", value: "mid", min: 200, max: 500 },
  { label: "500 – 1000 km", value: "long", min: 500, max: 1000 },
  { label: "> 1000 km", value: "epic", min: 1000, max: Infinity },
];

export default function RoutesList() {
  const [routes, setRoutes] = useState([]);
  const [filter, setFilter] = useState("All");
  const [query, setQuery] = useState("");
  const [country, setCountry] = useState("all");
  const [distanceBucket, setDistanceBucket] = useState("any");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api
      .get("/routes", { params: { page, page_size: pageSize } })
      .then(({ data }) => {
        const items = Array.isArray(data) ? data : data.items || [];
        if (!mounted) return;
        setRoutes(items);
        setTotal(data.total || 0);
        setLoading(false);
      })
      .catch(() => setLoading(false));
    return () => {
      mounted = false;
    };
  }, [page, pageSize]);

  const countries = useMemo(() => {
    const s = new Set(routes.map((r) => normalizeCountryValue(r.country)));
    return ["all", ...Array.from(s).sort()];
  }, [routes]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const bucket = distanceBuckets.find((b) => b.value === distanceBucket);
    return routes.filter((r) => {
      if (filter !== "All" && r.difficulty !== filter) return false;
      if (country !== "all" && normalizeCountryValue(r.country) !== country)
        return false;
      if (bucket && (r.distance_km < bucket.min || r.distance_km > bucket.max))
        return false;
      if (
        q &&
        !`${r.name} ${r.location} ${r.country} ${r.description}`
          .toLowerCase()
          .includes(q)
      )
        return false;
      return true;
    });
  }, [routes, filter, country, distanceBucket, query]);

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-24">
        <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#C86B4E]">
          Atlas
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl mt-3 max-w-3xl">
          Every route. Every film.
        </h1>
        <p className="mt-6 text-[#5C665F] max-w-2xl">
          Hand-picked bikepacking adventures. Filter by difficulty, country,
          distance — or search by name.
        </p>

        {/* search + filters */}
        <div className="mt-10 flex flex-col gap-4">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[240px] max-w-md">
              <MagnifyingGlass
                size={16}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-[#5C665F]"
              />
              <Input
                data-testid="search-input"
                placeholder="Search routes, locations…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-10 h-11 rounded-full bg-white border-[#E5E0D8]"
              />
            </div>

            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger
                className="w-[180px] h-11 rounded-full bg-white border-[#E5E0D8]"
                data-testid="country-filter"
              >
                <SelectValue placeholder="Country" />
              </SelectTrigger>
              <SelectContent>
                {countries.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c === "all"
                      ? "All countries"
                      : c === UNKNOWN_COUNTRY
                        ? "Unknown country"
                        : c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={distanceBucket} onValueChange={setDistanceBucket}>
              <SelectTrigger
                className="w-[180px] h-11 rounded-full bg-white border-[#E5E0D8]"
                data-testid="distance-filter"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {distanceBuckets.map((b) => (
                  <SelectItem key={b.value} value={b.value}>
                    {b.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-wrap gap-2" data-testid="difficulty-filter">
            {filters.map((f) => (
              <button
                key={f}
                data-testid={`filter-${f.toLowerCase()}`}
                onClick={() => setFilter(f)}
                className={`px-5 py-2 rounded-full text-sm font-bold border transition-colors ${
                  filter === f
                    ? "bg-[#2C5545] text-white border-[#2C5545]"
                    : "bg-transparent text-[#1A1D1A] border-[#E5E0D8] hover:border-[#2C5545]"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4 text-xs uppercase tracking-wider text-[#5C665F] font-bold">
          Showing {visible.length} of {total} routes
        </div>

        <div
          className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          data-testid="routes-grid"
        >
          {loading ? (
            <div className="col-span-3 text-center text-[#5C665F] py-20">
              Loading routes…
            </div>
          ) : (
            visible.map((r) => <RouteCard key={r.id} route={r} />)
          )}
        </div>

        {visible.length === 0 && !loading && (
          <div className="text-center text-[#5C665F] py-20">
            No routes match your filters. Try widening the search.
          </div>
        )}

        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
        />
      </div>
    </div>
  );
}
